import { FC, useState } from 'react';
import { toast } from 'react-toastify';
import { Info } from 'lucide-react';
import { Sizes } from 'enums/sizes';
import { useDialog } from 'hooks/useDialog';
import { useActionMenu } from 'components/navigation/Dropdown/GenericDropdownButton';
import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay';
import { funnelsAPI } from 'services/swagger-api';
import { useFunnelDetail } from '../FunnelDetailContext';
import { BlockCard, BlockRow, BlockRows } from 'back-office/shared/components/BlockCard';
import { DASH, formatAmount } from '../funnelFormat';
import { FunnelSettingsModal } from '../forms/FunnelSettingsModal';

/**
 * Bloc « Détail », en tête de page comme dans l'ancien écran.
 *
 * Les données que l'ancien formulaire proposait de modifier sans qu'elles soient
 * enregistrées (prélèvements, taux de commission, parties communes et
 * privatives) sont désormais marquées en lecture seule plutôt que masquées : elles
 * restent consultables, mais on n'invite plus à les saisir pour rien.
 */
export const DetailBlock: FC = () => {
  const { funnel, reloadFunnel } = useFunnelDetail();
  const { openBlankDialog } = useDialog();
  const { createMenuItem } = useActionMenu();
  const [isConfirmingStatus, setIsConfirmingStatus] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  const openSettings = () =>
    openBlankDialog(
      { size: Sizes.lg },
      <FunnelSettingsModal funnel={funnel} onSuccess={reloadFunnel} />
    );

  const toggleStatus = async () => {
    setIsTogglingStatus(true);
    try {
      const response = funnel.isActive
        ? await funnelsAPI.disableFunnel({ tunnelId: funnel.id })
        : await funnelsAPI.enableFunnel({ tunnelId: funnel.id });

      if (response.isSuccess) {
        toast.success(funnel.isActive ? 'Tunnel désactivé' : 'Tunnel activé');
        await reloadFunnel();
      } else {
        toast.error(response.errorMessage || 'Le changement de statut a échoué');
      }
    } catch (error) {
      console.error('Changement de statut impossible', error);
      toast.error('Le changement de statut a échoué');
    } finally {
      setIsTogglingStatus(false);
      setIsConfirmingStatus(false);
    }
  };

  const produitLabel = funnel.produit
    ? [funnel.produit.code, funnel.produit.libelle].filter(Boolean).join(' | ') || DASH
    : DASH;

  return (
    <>
      <BlockCard
        title="Détail"
        icon={<Info className="h-4 w-4" />}
        actions={[
          createMenuItem('edit', 'Modifier le tunnel', openSettings, { icon: 'edit' }),
          createMenuItem(
            'toggle',
            funnel.isActive ? 'Désactiver le tunnel' : 'Activer le tunnel',
            () => setIsConfirmingStatus(true),
            { icon: 'shield', variant: funnel.isActive ? 'danger' : 'default' }
          ),
        ]}
      >
        <div className="grid grid-cols-1 gap-x-10 lg:grid-cols-2">
          <BlockRows>
            <BlockRow label="Code">
              <span className="font-mono">{funnel.code || DASH}</span>
            </BlockRow>
            <BlockRow label="Désignation">{funnel.designation || DASH}</BlockRow>
            <BlockRow label="Produit">{produitLabel}</BlockRow>
            <BlockRow label="Formule">{funnel.formule?.designation || DASH}</BlockRow>
            <BlockRow label="Distributeur">{funnel.proprietaireDesignation || DASH}</BlockRow>
            <BlockRow label="Actif">{funnel.isActive ? 'Oui' : 'Non'}</BlockRow>
          </BlockRows>

          <BlockRows>
            <BlockRow label="Bénéficiaire">
              {funnel.beneficiaire?.personneDesignation || DASH}
            </BlockRow>
            <BlockRow label="IBAN">{funnel.beneficiaire?.iban || DASH}</BlockRow>
            <BlockRow label="Frais de résiliation">
              {formatAmount(funnel.fraisResiliation)}
            </BlockRow>
            <BlockRow label="Encaissement par le courtier autorisé">
              {funnel.brokerCollectionAllowed ? 'Oui' : 'Non'}
            </BlockRow>
          </BlockRows>
        </div>
      </BlockCard>

      <ConfirmationOverlay
        isOpen={isConfirmingStatus}
        onClose={() => setIsConfirmingStatus(false)}
        onConfirm={toggleStatus}
        isLoading={isTogglingStatus}
        variant={funnel.isActive ? 'danger' : 'default'}
        title={funnel.isActive ? 'Désactiver ce tunnel ?' : 'Activer ce tunnel ?'}
        description={
          funnel.isActive
            ? 'Le tunnel ne sera plus proposé à la souscription. Les contrats en cours ne sont pas affectés.'
            : 'Le tunnel redeviendra disponible à la souscription.'
        }
        confirmLabel={funnel.isActive ? 'Désactiver' : 'Activer'}
      />
    </>
  );
};
